import { UNPROCESSABLE_ENTITY } from '@mm/constants/status-codes';
import { getErrorJson } from '@mm/utils';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import ChatbotController, {
  BotCloneArgs,
} from '../controllers/ChatbotController';
import botHandler from '../errorHandlers/bot-handler';
import commonHandler from '../errorHandlers/common-handler';
import ChatbotModel, {
  ChatbotSetupStepsRemaining,
  ChatbotSetupStepStatus,
} from '../model/ChatbotModel';
import { PageKind } from '../model/PageModel';
import modalActions from '../modules/modalsState/actions';
import {
  activeChatBotAnySetupStepsRemaining,
  activeChatbotIdSelector,
  activeChatbotSelector,
  selectActiveChatbot,
  selectActiveCompanyId,
} from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import connectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';

const ACTIVE_CAMPAIGNS_ERROR_CODES = [999, 998];

export type CreateChatbotParams = {
  name: string;
  companyId: number;
  facebookPageId: number;
  onboarding?: boolean;
};

class ChatbotCRUDActions extends CrudActionContainer {
  cloneAction: string;
  errorsAction: string;
  resetErrorsAction: string;
  getLanguagesAction: string;
  attachToFacebookPageAction: string;
  resetChatbotsAction: string;
  retrievedBasicListAction: string;
  setFEActiveChatbotAction: string;
  polledAction: string;

  constructor() {
    super('CHATBOT');
    this.cloneAction = this.makeActionKey('CLONE');
    this.errorsAction = this.makeActionKey('ERRORS');
    this.resetErrorsAction = this.makeActionKey('RESET_ERRORS');
    this.getLanguagesAction = this.makeActionKey('LANGUAGES');
    this.attachToFacebookPageAction = this.makeActionKey(
      'ATTACH_TO_FACEBOOK_PAGE',
    );
    this.resetChatbotsAction = this.makeActionKey('RESET_CHATBOTS');
    this.retrievedBasicListAction = this.makeActionKey('RETRIEVED_BASIC_LIST');
    this.setFEActiveChatbotAction = this.makeActionKey('SET_FE_ACTIVE_CHATBOT');
    this.polledAction = this.makeActionKey('POLLED');
  }

  create(entity: CreateChatbotParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await ChatbotController.postChatbot(entity);
        dispatch(this.retrieveAll(entity.facebookPageId));
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  // double check if you're in need of chatbot state domain as some data is duplicated.
  // consider using connectedPageCRUDActions in case you're dealing with activeFacebookPage
  retrieve(
    botId: number,
    noActiveBotChange?: boolean,
    throwExceptions?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAction));
        const bot = await ChatbotController.getChatbot(botId);
        noActiveBotChange
          ? dispatch(this.polled(bot))
          : dispatch(this.retrieved(bot));
        return bot;
      } catch (e) {
        if (throwExceptions) {
          throw e;
        }
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(facebookPageId: number, lazy?: boolean) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        if (!lazy) {
          dispatch(this.busy(this.retrieveAllAction));
        }
        const response = await ChatbotController.getAll({ facebookPageId });
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveBasicListByCompany(companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch({ type: this.resetErrorsAction });
        dispatch(this.busy(this.retrieveAllAction));
        const response = await ChatbotController.getAll({
          companyId,
          light: true,
        });
        dispatch({
          type: this.retrievedBasicListAction,
          basicListByCompany: response,
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  resetChatbots() {
    return {
      type: this.resetChatbotsAction,
    };
  }

  setFEActiveChatbot(botId: number) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.setFEActiveChatbotAction,
        botId,
      });
    };
  }

  // MAY NOT NEED FOR THIS ITERATION OF NEW DASHBOARD
  activateBot(botId: number, facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));

      try {
        await ChatbotController.activateBot(botId, facebookPageId);
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  attachToFacebookPage(
    botId: number | null,
    facebookPageId: number,
    throwError?: boolean,
    force?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>, getState: any) => {
      try {
        dispatch(this.busy(this.updateAction));
        const activeCompanyId = selectActiveCompanyId(getState());
        await ChatbotController.attachBotToFacebookPage(
          botId,
          facebookPageId,
          force,
        );
        dispatch(connectedPageCRUDActions.retrievePage(facebookPageId));
        dispatch(
          connectedPageCRUDActions.retrieveAllByCompany(activeCompanyId),
        );
        await dispatch(this.retrieveAll(facebookPageId));
      } catch (error) {
        if (
          error instanceof Response &&
          error.status === UNPROCESSABLE_ENTITY
        ) {
          const json = await getErrorJson(error);
          const { error_code: errorCode } = json || {};

          if (ACTIVE_CAMPAIGNS_ERROR_CODES.includes(errorCode)) {
            dispatch(this.unbusy());
            return dispatch(
              modalActions.openGeneralConfirmModal({
                headerText: 'Pause active campaigns',
                messageText:
                  'Are you sure you want to pause this bot? Doing this will pause all your active campaigns.',
                onCloseHandler: ok => {
                  if (ok) {
                    dispatch(
                      this.attachToFacebookPage(
                        botId,
                        facebookPageId,
                        throwError,
                        true,
                      ),
                    );
                  }
                },
                okButtonText: "Yes I'm sure",
                cancelButtonText: 'Cancel',
              }),
            );
          }
        }

        if (throwError) {
          throw error;
        }
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  update(entity: Partial<ChatbotModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await ChatbotController.putChatbot(entity);
        dispatch(this.updateLocal(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  delete(id: number, facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteAction));
        const response = await ChatbotController.deleteChatbot(id);
        dispatch(this.retrieveAll(facebookPageId));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  cloneBot(cloneArgs: BotCloneArgs) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        // TODO: Fix strict null error
        // @ts-ignore
        const response = await ChatbotController.clone(cloneArgs);
        // TODO: Fix strict null error
        // @ts-ignore
        dispatch(this.retrieveAll(cloneArgs.facebookPageId));
        return response;
      } catch (e) {
        return botHandler(dispatch)(e);
      }
    };
  }

  togglePlatformChannels(
    ...args: Parameters<typeof ChatbotController.togglePlatformChannels>
  ) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const activeBot = selectActiveChatbot(getState());

      dispatch(this.busy());
      try {
        const res = await ChatbotController.togglePlatformChannels(...args);
        const { platformsChannels, success, errors = [] } = res;
        if (
          !success &&
          (errors?.code || []).some(v =>
            ACTIVE_CAMPAIGNS_ERROR_CODES.includes(v),
          )
        ) {
          dispatch(
            modalActions.openGeneralConfirmModal({
              headerText: 'Pause active campaigns',
              messageText:
                // tslint:disable-next-line:max-line-length
                'Are you sure you want to pause all bot activity on this channel?\n Once you pause this channel, all your active campaigns will be paused',
              onCloseHandler: ok => {
                if (ok) {
                  const [params] = args;
                  dispatch(
                    this.togglePlatformChannels({
                      ...params,
                      force: true,
                    }),
                  );
                }
              },
              okButtonText: "Yes I'm sure",
              cancelButtonText: 'Cancel',
            }),
          );
        }

        if (success && platformsChannels) {
          dispatch(this.retrieved({ ...activeBot, platformsChannels }));
        }
      } catch (err) {
        await commonHandler(dispatch)(err);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getLanguages() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.getLanguagesAction));

      try {
        const response = await ChatbotController.languages();
        dispatch({
          type: this.getLanguagesAction,
          entity: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  updateSetupStepsRemaining(
    step: keyof ChatbotSetupStepsRemaining,
    status: ChatbotSetupStepStatus,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const { id: chatbotId, setupStepsRemaining } =
        activeChatbotSelector(state);
      if (
        !setupStepsRemaining ||
        setupStepsRemaining[step] === status ||
        setupStepsRemaining[step] === 'dismissed'
      ) {
        return;
      }
      dispatch(
        this.update({
          id: chatbotId,
          setupStepsRemaining: {
            ...setupStepsRemaining,
            [step]: status,
          },
        }),
      );
    };
  }

  updateSetupStepsRemainingByPageIdUpdated(pageIdUpdated: number) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const { setupStepsRemaining } = activeChatbotSelector(state);
      const anyStepsRemaining = activeChatBotAnySetupStepsRemaining(state);
      if (!setupStepsRemaining || !anyStepsRemaining) {
        // No setup setps remaining so nothing to do.
        return;
      }
      if (setupStepsRemaining.chatWidgetUpdate) {
        const promoter = state.promoter.entities.find(
          p => p.pageId === pageIdUpdated && p.type === 'customer_chat',
        );
        if (promoter) {
          dispatch(
            this.updateSetupStepsRemaining('chatWidgetUpdate', 'completed'),
          );
        }
      }
      if (setupStepsRemaining.fbWelcomerUpdate) {
        const page = state.page.entities.find(
          p => p.id === pageIdUpdated && p.kind === PageKind.WelcomePage,
        );
        if (page) {
          dispatch(
            this.updateSetupStepsRemaining('fbWelcomerUpdate', 'completed'),
          );
        }
      }
    };
  }

  updateGlobalJS(botIdOverride?: number) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const botId = botIdOverride
        ? botIdOverride
        : activeChatbotIdSelector(getState());
      try {
        this.busy();
        await ChatbotController.updateGlobalJS(botId);
      } finally {
        this.unbusy();
      }
    };
  }

  getWebchatToken() {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const chatbot = activeChatbotSelector(getState());
      try {
        this.busy();
        const result = await ChatbotController.getWebchatToken(chatbot.id);
        dispatch(
          this.updateLocal({ ...chatbot, webchatToken: result.webchatToken }),
        );
      } finally {
        this.unbusy();
      }
    };
  }
}

const chatbotCRUDActions = new ChatbotCRUDActions();

export default chatbotCRUDActions;

export const useChatbotCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      create: (entity: CreateChatbotParams) =>
        dispatch(chatbotCRUDActions.create(entity)),
      update: (entity: Partial<ChatbotModel>) =>
        dispatch(chatbotCRUDActions.update(entity)),
      togglePlatformChannels: (
        ...args: Parameters<typeof chatbotCRUDActions.togglePlatformChannels>
      ) => dispatch(chatbotCRUDActions.togglePlatformChannels(...args)),
      updateSetupStepsRemaining: (
        step: keyof ChatbotSetupStepsRemaining,
        status: ChatbotSetupStepStatus,
      ) => dispatch(chatbotCRUDActions.updateSetupStepsRemaining(step, status)),
      updateSetupStepsRemainingByPageIdUpdated: (pageIdUpdated: number) =>
        dispatch(
          chatbotCRUDActions.updateSetupStepsRemainingByPageIdUpdated(
            pageIdUpdated,
          ),
        ),
      attachToFacebookPage: (
        botId: number | null,
        facebookPageId: number,
        throwError?: boolean,
      ) =>
        dispatch(
          chatbotCRUDActions.attachToFacebookPage(
            botId,
            facebookPageId,
            throwError,
          ),
        ),
      updateGlobalJS: (botIdOverride?: number) =>
        dispatch(chatbotCRUDActions.updateGlobalJS(botIdOverride)),
      getLanguages: () => dispatch(chatbotCRUDActions.getLanguages()),
      retrieveAll: (facebookPageId: number, lazy?: boolean) =>
        dispatch(chatbotCRUDActions.retrieveAll(facebookPageId, lazy)),
      retrieve: (...args: Parameters<typeof chatbotCRUDActions.retrieve>) =>
        dispatch(chatbotCRUDActions.retrieve(...args)),
      getWebchatToken: () => dispatch(chatbotCRUDActions.getWebchatToken()),
      retrieveBasicListByCompany: (
        ...args: Parameters<
          typeof chatbotCRUDActions.retrieveBasicListByCompany
        >
      ) => dispatch(chatbotCRUDActions.retrieveBasicListByCompany(...args)),
      setFEActiveChatbot: (
        ...args: Parameters<typeof chatbotCRUDActions.setFEActiveChatbot>
      ) => dispatch(chatbotCRUDActions.setFEActiveChatbot(...args)),
    }),
    [dispatch],
  );
};
